import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Button, Icon, Spinner } from '@amzn/awsui-components-console';
import { styled } from '@linaria/react';
import { fetchMidwayToken } from '../datafetchers/fetchMidwayToken';
import { QueryKeys } from '../constants/queryKeys';
import { CenteredFullPageMessage } from '../components/primitives/CenteredFullPageMessage';
import { MidwayContext } from './Midway';

const TIMEOUT_BUFFER_SECONDS = 30;
const RETRY_ATTEMPTS = 3;

const CutTicketButton = styled(Button)`
  margin-left: 5rem;
`;

/**
 * Fetch and provide a midway auth token to the application.
 *
 * **Testing Note**: This provider should be used directly in unit tests
 * rather than mocked. For testing, you can mock the fetchMidwayToken
 * function to return test data or simulate error conditions.
 *
 * The provider gracefully handles loading, error, and success states,
 * making it safe for testing environments. It also includes automatic
 * token refresh logic that can be tested by controlling the mock responses.
 *
 * Example usage in tests:
 * ```tsx
 * // Mock the fetchMidwayToken function
 * jest.mock('../datafetchers/fetchMidwayToken', () => ({
 *   fetchMidwayToken: jest.fn(() => Promise.resolve({
 *     token: 'mock-token',
 *     decodedToken: { exp: Date.now() / 1000 + 3600, username: 'testuser' }
 *   }))
 * }));
 *
 * render(
 *   <QueryClient client={queryClient}>
 *     <MidwayProvider>
 *       <YourComponent />
 *     </MidwayProvider>
 *   </QueryClient>
 * );
 * ```
 *
 * For error testing, mock fetchMidwayToken to reject:
 * ```tsx
 * fetchMidwayToken.mockRejectedValue(new Error('Auth failed'));
 * ```
 */
export function MidwayProvider(props: { readonly children: React.ReactNode }) {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: [QueryKeys.MIDWAY],
    queryFn: () => fetchMidwayToken(),
    retry: RETRY_ATTEMPTS,
    refetchIntervalInBackground: true,
    refetchInterval: ({ state: { data } }) => {
      const expiration = data?.decodedToken.exp;
      if (expiration !== undefined) {
        return ((expiration * 1000 - Date.now()) / 1000 - TIMEOUT_BUFFER_SECONDS) * 1000;
      }
      return 0;
    },
  });

  React.useEffect(() => {
    if (error !== null) {
      console.error(new Error('Error fetching Midway token', { cause: error }));
    }
  }, [error]);

  return (
    <MidwayContext.Provider value={data ?? null}>
      {isLoading ? (
        <CenteredFullPageMessage>
          <Spinner size='large' />
          Loading Midway credentials...
        </CenteredFullPageMessage>
      ) : isError ? (
        <CenteredFullPageMessage>
          <Icon name='close' size='large' variant='error' />
          <div>
            Unable to load Midway credentials.
            <br />
            Please cut us a ticket for assistance.
          </div>
          <CutTicketButton
            target='_blank'
            href='https://t.corp.amazon.com/create/options?category=AWS&type=GroundStation&item=GSOC'
          >
            Cut Ticket
          </CutTicketButton>
        </CenteredFullPageMessage>
      ) : (
        props.children
      )}
    </MidwayContext.Provider>
  );
}
